<template>
  <tr>
    <td>
      <!--Description -->
    <input
      class="field-input"
      v-model="productName"
      @change="updateRow"
      disabled
    />
    </td>
    <td>
      <!--Row amount -->
      <input
        class="field-input"
        type="number"
        v-model="amount"
        @change="updateRow"
        min="0"
    /></td>
    <td>
      <!--Vat code -->
  <select class="field-select" v-model="selectedVatCode" @change="onVatCodeChange">
        <option value="" disabled>Select a VAT code</option>
        <option v-for="vatCode in vatCodes" :key="vatCode.id" :value="vatCode.id">
          {{ vatCode.name }}
        </option>
      </select>
    </td>
    <td>
      <!--Vat percent -->
    <select
      class="field-select"
      v-model="selectedVatPercent"
      @change="updateRow"
      :disabled="!availablePercentages.length"
    >
        <option value="" disabled>Select percentage</option>
        <option v-for="percentage in availablePercentages" :key="percentage" :value="percentage">
          {{ percentage }}%
        </option>
      </select>
    </td>
    <td>
      <!--Account selection -->
  <select class="field-select" v-model="selectedAccount" @change="updateRow">
        <option value="" disabled>Select an account</option>
        <option v-for="account in accounts" :key="account.id" :value="account.id">
          {{ account.accountNumber + ' ' + account.accountName }}
        </option>
      </select>
    </td>
    <!--Dimensions -->
 <td v-for="(dimension, index) in dimensions" :key="dimension.id">
  <select class="field-select"
    :value="selectedDimensions[index].id"
    @change="(e) => onDimensionChange(index, dimension.options?.find(o => o.id === (e.target as HTMLSelectElement).value) || {id: '', name: ''})"
  >
    <option value="" disabled>-</option>
    <option v-for="dimensionOption in dimension.options" :key="dimensionOption.id" :value="dimensionOption.id">
      {{ dimensionOption.name }}
    </option>
  </select>
</td>
    <!--
<td>
    <button @click="removeRow" class="delete-btn" aria-label="Delete row">Delete</button>
    </td>
    -->
  </tr>
</template>
<script lang="ts">
import {computed, defineComponent, PropType, ref, watch} from 'vue';
import {Account, AccountingRow, Dimension, DimensionOption, VatCode} from "@/types/invoice";

export default defineComponent({
  name: 'InvoiceAccountingRow',
  props: {
    row: {
      type: Object as PropType<AccountingRow>,
      required: true
    },
    accounts: {
      type: Array as PropType<Account[]>,
      required: true
    },
    dimensions: {
      type: Array as PropType<Dimension[]>,
      required: true
    },
    vatCodes: {
      type: Array as PropType<VatCode[]>,
      required: true
    }
  },
  emits: ['updateRow', 'removeRow'],
  setup(props, {emit}) {
    const amount = ref(props.row.amount);
    const productName = ref(props.row.productName || '');
    const selectedAccount = ref(props.row.accountId || '');
    const selectedVatCode = ref(props.row.vatCode || '');
    const selectedVatPercent = ref(props.row.vatPercent || '');
    const selectedDimensions = ref(
      props.dimensions.map(dimension => {
        const matchingDimension = props.row.dimensions?.find(
          d => d.parentDimensionId === dimension.id
        );
        return {
          parentDimensionId: dimension.id,
          id: matchingDimension?.id || '',
          name: matchingDimension?.name || ''
        };
      })
    );

  // Computed property to get available percentages for the selected vat code
    const availablePercentages = computed(() => {
      if (!selectedVatCode.value) return [];

      const selectedVat = props.vatCodes.find(vat => vat.id === selectedVatCode.value);
      return selectedVat?.availableNumbers?.map(num => String(num)) || [];
    });

    // Computed property to get the current selected vat code object
    const currentVatCode = computed(() => {
      return props.vatCodes.find(vat => vat.id === selectedVatCode.value);
    });

    // Watch for external changes to the row
    watch(() => props.row, (newRow) => {
      amount.value = newRow.amount;
      productName.value = newRow.productName || '';
      selectedAccount.value = newRow.accountId || '';
      selectedVatCode.value = newRow.vatCode || '';
      selectedVatPercent.value = newRow.vatPercent || '';

     // Update dimensions when row changes
      selectedDimensions.value = props.dimensions.map(dimension => {
        const matchingDimension = newRow.dimensions?.find(
          d => d.parentDimensionId === dimension.id
        );
        return {
          parentDimensionId: dimension.id,
          id: matchingDimension?.id || '',
          name: matchingDimension?.name || ''
        };
      });
    }, {deep: true});

    const onDimensionChange = (dimensionIndex: number, option: DimensionOption) => {
      selectedDimensions.value[dimensionIndex] = {
        parentDimensionId: props.dimensions[dimensionIndex].id,
        id: option.id,
        name: option.name
      };
      updateRow();
    };

    // Handle vat code change
    const onVatCodeChange = () => {
      const selectedVat = currentVatCode.value;
      // Set default percent if available
      if (selectedVat?.defaultPercent !== undefined) {
        selectedVatPercent.value = selectedVat.defaultPercent.toString();
      } else if (availablePercentages.value.length > 0) {
        // If no default percent but there are available numbers, select the first one
        selectedVatPercent.value = String(availablePercentages.value[0]);
      } else {
        // Clear selection if no available percentages
        selectedVatPercent.value = '';
      }

      updateRow();
    };

    const updateRow = () => {
      emit('updateRow', {
        ...props.row,
        amount: Number(amount.value),
        accountId: selectedAccount.value,
        productName: productName.value,
        vatCode: selectedVatCode.value,
        vatPercent: selectedVatPercent.value || '',
        dimensions: selectedDimensions.value.filter(d => d.id)
      });
    };

    const removeRow = () => {
      emit('removeRow', props.row.id);
    };

    return {
      selectedAccount,
      selectedVatCode,
      selectedVatPercent,
      selectedDimensions,
      amount,
      productName,
      availablePercentages,
      currentVatCode,
      onVatCodeChange,
      updateRow,
      removeRow,
      onDimensionChange
    };
  }

});
</script>
<style scoped>
.field-input,
.field-select {
  width: 100%;
  box-sizing: border-box;
  padding: 6px 8px;
  font-size: 0.95rem;
  border: 1px solid #d0d0d0;
  border-radius: 4px;
  background-color: #fff;
  color: #222;
}

.field-input:focus,
.field-select:focus {
  outline: none;
  border-color: #7aa7ff;
  box-shadow: 0 0 0 3px rgba(122,167,255,0.12);
}

/* Remove spinner controls for number inputs in WebKit browsers */
input[type=number]::-webkit-outer-spin-button,
input[type=number]::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

/* Remove spinner controls in Firefox */
input[type=number] {
  appearance: textfield;
  -moz-appearance: textfield;
}

/* Style for disabled (readonly) input fields */
.accounting-table input[disabled],
.accounting-table input:disabled {
  background-color: #f5f5f5;
  color: #333333;
  border: 1px dashed #bbb;
  cursor: not-allowed;
}

</style>